import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styles from './MicrositePopupEditor.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import TextInput from '../components/TextInput/TextInput'
import SelectInput from '../components/SelectInput/SelectInput'
import ToggleSwitch from '../components/ToggleSwitch/ToggleSwitch'
import MicrositeImageUploader from '../components/MicrositeImageUploader/MicrositeImageUploader'
import MicrositeDateRangePicker from '../components/MicrositeDateRangePicker/MicrositeDateRangePicker'
import MicrositePageMultiSelect from '../components/MicrositePageMultiSelect/MicrositePageMultiSelect'
import MicrositePopupPreview from '../components/MicrositePopupPreview/MicrositePopupPreview'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'

const TRIGGER_OPTIONS = [
    { value: 'EXIT_INTENT', label: 'Exit intent' },
    { value: 'DELAY', label: 'Time delay' },
    { value: 'SCROLL', label: 'Scroll depth' },
    { value: 'MANUAL', label: 'Manual trigger only' }
]

const FREQUENCY_OPTIONS = [
    { value: 'ONCE_PER_SESSION', label: 'Once per session' },
    { value: 'ONCE_PER_DAY', label: 'Once per day' },
    { value: 'ALWAYS', label: 'Every page load' }
]

const AVAILABLE_PAGES = [
    { slug: '/', label: 'Home' },
    { slug: '/about-us', label: 'About us' },
    { slug: '/contact', label: 'Contact' },
    { slug: '/blog', label: 'Blog' }
]

const EMPTY_FORM = {
    popupName: '',
    triggerType: 'EXIT_INTENT',
    triggerValue: 0,
    content: { heading: '', body: '', image: '', cta: { label: '', url: '' } },
    showOnPages: null,
    showFrequency: 'ONCE_PER_SESSION',
    startDate: '',
    endDate: '',
    isActive: true
}

const MicrositePopupEditor = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const isNew = id === 'new' || !id

    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    // Note: there's no dedicated "get one popup" API in the current
    // contract — fetching the list and matching by ID client-side,
    // same pattern as MicrositeSeoPageEditor.
    const loadExisting = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetPopups', { limit: 100 })
        if (res?.status === 'success') {
            const match = (res.response?.data ?? []).find((r) => String(r.ID) === String(id))
            if (match) {
                const next = {
                    popupName: match.popupName || '',
                    triggerType: match.triggerType || 'EXIT_INTENT',
                    triggerValue: match.triggerValue ?? 0,
                    content: match.content || EMPTY_FORM.content,
                    showOnPages: match.showOnPages ?? null,
                    showFrequency: match.showFrequency || 'ONCE_PER_SESSION',
                    startDate: match.startDate ? match.startDate.slice(0, 10) : '',
                    endDate: match.endDate ? match.endDate.slice(0, 10) : '',
                    isActive: match.isActive ?? true
                }
                setForm(next)
                setOriginal(next)
            } else {
                showToast('Could not find this popup', 'error')
            }
        } else {
            showToast('Could not load popup. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post, id])

    useEffect(() => {
        if (!isNew && activeStore?.storeIdentifier) loadExisting()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, id])

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))
    const updateContent = (field, value) => setForm((prev) => ({ ...prev, content: { ...prev.content, [field]: value } }))
    const updateCta = (field, value) => setForm((prev) => ({ ...prev, content: { ...prev.content, cta: { ...prev.content.cta, [field]: value } } }))

    const handleSave = async () => {
        if (!form.popupName.trim()) {
            showToast('Popup name is required', 'error')
            return
        }
        setSaving(true)

        const payload = { ...form }

        const res = isNew
            ? await post('micrositeCreatePopup', payload)
            : await post('micrositeUpdatePopup', { ID: id, ...payload })

        setSaving(false)

        if (res?.status === 'success') {
            showToast('Popup saved successfully')
            setOriginal(form)
            navigate('/microsite/popups')
        } else {
            showToast('Could not save popup. Please try again.', 'error')
        }
    }

    const handleDelete = async () => {
        const res = await post('micrositeDeletePopup', { ID: id })
        if (res?.status === 'success') {
            showToast('Popup deleted')
            navigate('/microsite/popups')
        } else {
            showToast('Could not delete popup', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading popup…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title={isNew ? 'New popup' : 'Edit popup'}
                backTo="/microsite/popups"
                backLabel="Popups"
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onDelete={!isNew ? () => setDeleteConfirmOpen(true) : undefined}
            />

            <div className={styles.layout}>
                <div className={styles.formCol}>
                    <section className={styles.fieldGroup}>
                        <TextInput
                            label="Popup name"
                            value={form.popupName}
                            onChange={(v) => update('popupName', v)}
                            placeholder="e.g. First-time visitor offer"
                            required
                            helperText="Internal name — not shown to visitors"
                        />

                        <div className={styles.row2}>
                            <SelectInput
                                label="Trigger type"
                                value={form.triggerType}
                                onChange={(v) => update('triggerType', v)}
                                options={TRIGGER_OPTIONS}
                            />
                            {(form.triggerType === 'DELAY' || form.triggerType === 'SCROLL') && (
                                <TextInput
                                    label={form.triggerType === 'DELAY' ? 'Delay (seconds)' : 'Scroll depth (%)'}
                                    value={form.triggerValue}
                                    onChange={(v) => update('triggerValue', Number(v) || 0)}
                                    type="number"
                                />
                            )}
                        </div>

                        <SelectInput
                            label="Show frequency"
                            value={form.showFrequency}
                            onChange={(v) => update('showFrequency', v)}
                            options={FREQUENCY_OPTIONS}
                        />

                        <MicrositePageMultiSelect
                            label="Show on pages"
                            value={form.showOnPages}
                            onChange={(v) => update('showOnPages', v)}
                            availablePages={AVAILABLE_PAGES}
                        />

                        <MicrositeDateRangePicker
                            label="Active dates (optional)"
                            fromValue={form.startDate}
                            toValue={form.endDate}
                            onFromChange={(v) => update('startDate', v)}
                            onToChange={(v) => update('endDate', v)}
                        />

                        <ToggleSwitch
                            label="Active"
                            description="Popup will show to visitors when conditions are met"
                            checked={form.isActive}
                            onChange={(v) => update('isActive', v)}
                        />
                    </section>

                    <section className={styles.fieldGroup}>
                        <h2 className={styles.groupTitle}>Content</h2>

                        <TextInput
                            label="Heading"
                            value={form.content.heading}
                            onChange={(v) => updateContent('heading', v)}
                            placeholder="e.g. Get 20% off your first visit"
                        />
                        <TextInput
                            label="Body text"
                            value={form.content.body}
                            onChange={(v) => updateContent('body', v)}
                            placeholder="A short, persuasive message"
                        />
                        <MicrositeImageUploader
                            label="Image (optional)"
                            value={form.content.image}
                            onChange={(v) => updateContent('image', v)}
                            placeholder="https://example.com/popup-image.jpg"
                        />
                        <div className={styles.row2}>
                            <TextInput
                                label="Button text"
                                value={form.content.cta?.label}
                                onChange={(v) => updateCta('label', v)}
                                placeholder="Claim offer"
                            />
                            <TextInput
                                label="Button link"
                                value={form.content.cta?.url}
                                onChange={(v) => updateCta('url', v)}
                                placeholder="/book"
                            />
                        </div>
                    </section>
                </div>

                <div className={styles.previewCol}>
                    <div className={styles.previewSticky}>
                        <MicrositePopupPreview content={form.content} />
                    </div>
                </div>
            </div>

            <MicrositeDeleteConfirm
                mode="modal"
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                onConfirm={handleDelete}
                itemLabel="this popup"
            />
        </div>
    )
}

export default MicrositePopupEditor